import React from 'react';
import { DollarSign, TrendingUp, AlertTriangle } from 'lucide-react';

/**
 * GW Profitability Results Component
 * Replicates the Excel "GW Profitability Calc" tab layout
 * Two-panel view: Gross Fees Calculation (left) and Net Fees Calculation (right)
 */
export default function GWProfitabilityResults({ profitabilityData, userRole = 'preparer' }) {
  // Merchant fee columns (Merchant Fees + Implied MF) are only visible to CFO/CEO/Admin
  const canSeeMerchantFees = ['cfo', 'ceo', 'admin'].includes(userRole);
  // Debug logging
  console.log('[GWProfitabilityResults] Received profitabilityData:', profitabilityData);
  
  // Check if we have incumbent comparison data
  const hasIncumbentComparison = profitabilityData && profitabilityData.incumbent_comparison;
  const incumbentComparison = hasIncumbentComparison ? profitabilityData.incumbent_comparison : null;
  
  if (!profitabilityData || !profitabilityData.success) {
    return (
      <div className="card p-6">
        <p className="text-slate-600">
          {profitabilityData?.error || 'Unable to calculate profitability. Please check your inputs.'}
        </p>
      </div>
    );
  }

  const { payment_types = {}, totals = {}, net_fees = {} } = profitabilityData || {};
  
  // Debug logging for data structure
  console.log('[GWProfitabilityResults] Payment Types:', payment_types);
  console.log('[GWProfitabilityResults] CC:', payment_types.CC);
  console.log('[GWProfitabilityResults] DEBIT:', payment_types.DEBIT);
  console.log('[GWProfitabilityResults] AMEX:', payment_types.AMEX);
  console.log('[GWProfitabilityResults] ACH:', payment_types.ACH);
  console.log('[GWProfitabilityResults] Totals:', totals);
  console.log('[GWProfitabilityResults] Totals.total_payments:', totals.total_payments);
  console.log('[GWProfitabilityResults] Net Fees:', net_fees);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    return `${value.toFixed(2)}%`;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Show CC (Non-Amex Credit Cards) separately, not combined with Debit
  const creditCardPayments = payment_types.CC || null;

  // Calculate totals by summing the displayed payment type rows
  const displayedTotals = {
    total_payments: (payment_types.CC?.total_payments || 0) + 
                    (payment_types.AMEX?.total_payments || 0) + 
                    (payment_types.DEBIT?.total_payments || 0) + 
                    (payment_types.ACH?.total_payments || 0),
    total_fees: (payment_types.CC?.total_fees || 0) + 
                (payment_types.AMEX?.total_fees || 0) + 
                (payment_types.DEBIT?.total_fees || 0) + 
                (payment_types.ACH?.total_fees || 0),
    merchant_fees: (payment_types.CC?.merchant_fees || 0) + 
                   (payment_types.AMEX?.merchant_fees || 0) + 
                   (payment_types.DEBIT?.merchant_fees || 0) + 
                   (payment_types.ACH?.merchant_fees || 0),
    net_revenue: (payment_types.CC?.net_revenue || 0) + 
                 (payment_types.AMEX?.net_revenue || 0) + 
                 (payment_types.DEBIT?.net_revenue || 0) + 
                 (payment_types.ACH?.net_revenue || 0)
  };

  // Calculate percentage metrics for totals (excluding ACH)
  const nonAchPayments = (payment_types.CC?.total_payments || 0) + 
                         (payment_types.AMEX?.total_payments || 0) + 
                         (payment_types.DEBIT?.total_payments || 0);
  const nonAchFees = (payment_types.CC?.total_fees || 0) + 
                     (payment_types.AMEX?.total_fees || 0) + 
                     (payment_types.DEBIT?.total_fees || 0);
  const nonAchMerchantFees = (payment_types.CC?.merchant_fees || 0) + 
                             (payment_types.AMEX?.merchant_fees || 0) + 
                             (payment_types.DEBIT?.merchant_fees || 0);
  const nonAchNetRevenue = (payment_types.CC?.net_revenue || 0) + 
                           (payment_types.AMEX?.net_revenue || 0) + 
                           (payment_types.DEBIT?.net_revenue || 0);

  displayedTotals.implied_tr = totals.implied_tr || 0;  // Use backend-computed value (midpoint-based denominator, min-fee-inclusive)
  displayedTotals.implied_mf = nonAchFees > 0 ? (nonAchMerchantFees / nonAchFees * 100) : 0;
  displayedTotals.gpm = nonAchFees > 0 ? (nonAchNetRevenue / nonAchFees * 100) : 0;

  // Detect if a total payments override is active on any card payment type
  const overrideInfo = payment_types.CC?.override_info || payment_types.AMEX?.override_info || payment_types.DEBIT?.override_info;
  const overrideActive = overrideInfo?.active === true;
  const overrideFactor = overrideActive ? (overrideInfo.adjustment_factor || 1) : 1;
  const overrideInputTotal = overrideActive ? (overrideInfo.input_total_payments || 0) : 0;
  const overrideCalcTotal = overrideActive ? (overrideInfo.calculated_total_payments || 0) : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-l-blue-500">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <TrendingUp className="w-7 h-7 text-blue-600" />
              GW Projected Profitability
            </h2>
            <p className="text-slate-600 mt-1">{new Date().toLocaleDateString()}</p>
          </div>
        </div>
      </div>

      {/* Override Active Banner */}
      {overrideActive && (
        <div
          data-testid="override-active-banner"
          className="flex items-start gap-3 px-5 py-4 rounded-xl border border-amber-300 bg-amber-50"
          style={{ borderLeft: '4px solid #f59e0b' }}
        >
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              Total Payments Override Active &nbsp;
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-amber-200 text-amber-900">
                {overrideFactor.toFixed(2)}× multiplier
              </span>
            </p>
            <p className="text-xs text-amber-800 mt-0.5">
              Block 2 input: <strong>${(overrideInputTotal / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}K</strong> &nbsp;·&nbsp;
              Auto-calculated (midpoints): <strong>${(overrideCalcTotal / 1000).toLocaleString('en-US', { maximumFractionDigits: 0 })}K</strong>
              &nbsp;·&nbsp; All fee calculations (Payments, Fees, Merchant Fees) use midpoint-based amounts. Implied TR denominator is also midpoint-based.
            </p>
          </div>
        </div>
      )}

      {/* Stacked Layout: Gross Fees & Net Fees */}
      <div className="space-y-6">
        
        {/* LEFT PANEL: Gross Fees Calculation */}
        <div className="card p-6">
          <div className="bg-blue-600 text-white px-4 py-3 rounded-t-lg -mx-6 -mt-6 mb-6">
            <h3 className="text-lg font-bold">Gross Fees Calculation</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left p-2 font-semibold">Payment Type</th>
                  <th className="text-right p-2 font-semibold">Payments</th>
                  <th className="text-right p-2 font-semibold">Fees</th>
                  <th className="text-right p-2 font-semibold">Implied TR (%)</th>
                  {canSeeMerchantFees && <th className="text-right p-2 font-semibold">Merchant Fees</th>}
                  {canSeeMerchantFees && <th className="text-right p-2 font-semibold">Implied MF</th>}
                  <th className="text-right p-2 font-semibold">Net Revenue</th>
                  <th className="text-right p-2 font-semibold">GPM</th>
                </tr>
              </thead>
              <tbody>
                {/* Credit Card (Non-Amex) Payments */}
                {creditCardPayments && (
                  <tr className="bg-blue-50 hover:bg-blue-100">
                    <td className="p-2 font-medium text-blue-900">Non-Amex Payments</td>
                    <td className="text-right p-2">{formatCurrency(creditCardPayments.total_payments)}</td>
                    <td className="text-right p-2 font-semibold">{formatCurrency(creditCardPayments.total_fees)}</td>
                    <td className="text-right p-2">{formatPercent(creditCardPayments.implied_tr)}</td>
                    {canSeeMerchantFees && <td className="text-right p-2">{formatCurrency(creditCardPayments.merchant_fees)}</td>}
                    {canSeeMerchantFees && <td className="text-right p-2">{formatPercent(creditCardPayments.implied_mf)}</td>}
                    <td className="text-right p-2 font-semibold">{formatCurrency(creditCardPayments.net_revenue)}</td>
                    <td className="text-right p-2 font-semibold text-green-700">{formatPercent(creditCardPayments.gpm)}</td>
                  </tr>
                )}

                {/* AMEX Payments */}
                {payment_types.AMEX && (
                  <tr className="bg-blue-50 hover:bg-blue-100">
                    <td className="p-2 font-medium text-blue-900">AMEX Payments</td>
                    <td className="text-right p-2">{formatCurrency(payment_types.AMEX.total_payments)}</td>
                    <td className="text-right p-2 font-semibold">{formatCurrency(payment_types.AMEX.total_fees)}</td>
                    <td className="text-right p-2">{formatPercent(payment_types.AMEX.implied_tr)}</td>
                    {canSeeMerchantFees && <td className="text-right p-2">{formatCurrency(payment_types.AMEX.merchant_fees)}</td>}
                    {canSeeMerchantFees && <td className="text-right p-2">{formatPercent(payment_types.AMEX.implied_mf)}</td>}
                    <td className="text-right p-2 font-semibold">{formatCurrency(payment_types.AMEX.net_revenue)}</td>
                    <td className="text-right p-2 font-semibold text-green-700">{formatPercent(payment_types.AMEX.gpm)}</td>
                  </tr>
                )}

                {/* Debit Card Payments */}
                {payment_types.DEBIT && payment_types.DEBIT.total_payments > 0 && (
                  <tr className="bg-blue-50 hover:bg-blue-100">
                    <td className="p-2 font-medium text-blue-900">Debit Card Payments</td>
                    <td className="text-right p-2">{formatCurrency(payment_types.DEBIT.total_payments)}</td>
                    <td className="text-right p-2 font-semibold">{formatCurrency(payment_types.DEBIT.total_fees)}</td>
                    <td className="text-right p-2">{formatPercent(payment_types.DEBIT.implied_tr)}</td>
                    {canSeeMerchantFees && <td className="text-right p-2">{formatCurrency(payment_types.DEBIT.merchant_fees)}</td>}
                    {canSeeMerchantFees && <td className="text-right p-2">{formatPercent(payment_types.DEBIT.implied_mf)}</td>}
                    <td className="text-right p-2 font-semibold">{formatCurrency(payment_types.DEBIT.net_revenue)}</td>
                    <td className="text-right p-2 font-semibold text-green-700">{formatPercent(payment_types.DEBIT.gpm)}</td>
                  </tr>
                )}

                {/* ACH Payments */}
                {payment_types.ACH && (
                  <tr className="bg-blue-50 hover:bg-blue-100">
                    <td className="p-2 font-medium text-blue-900">ACH Payments</td>
                    <td className="text-right p-2">{formatCurrency(payment_types.ACH.total_payments)}</td>
                    <td className="text-right p-2 font-semibold">{formatCurrency(payment_types.ACH.total_fees)}</td>
                    <td className="text-right p-2">{formatPercent(payment_types.ACH.implied_tr)}</td>
                    {canSeeMerchantFees && <td className="text-right p-2">{formatCurrency(payment_types.ACH.merchant_fees)}</td>}
                    {canSeeMerchantFees && <td className="text-right p-2">{formatPercent(payment_types.ACH.implied_mf)}</td>}
                    <td className="text-right p-2 font-semibold">{formatCurrency(payment_types.ACH.net_revenue)}</td>
                    <td className="text-right p-2 font-semibold text-green-700">{formatPercent(payment_types.ACH.gpm)}</td>
                  </tr>
                )}

                {/* Totals Row - Sum of displayed payment types */}
                <tr className="bg-green-100 font-bold border-t-2 border-green-600">
                  <td className="p-2 text-green-900">Totals</td>
                  <td className="text-right p-2">{formatCurrency(displayedTotals.total_payments)}</td>
                  <td className="text-right p-2 text-green-900">{formatCurrency(displayedTotals.total_fees)}</td>
                  <td className="text-right p-2">{formatPercent(displayedTotals.implied_tr)}</td>
                  {canSeeMerchantFees && <td className="text-right p-2">{formatCurrency(displayedTotals.merchant_fees)}</td>}
                  {canSeeMerchantFees && <td className="text-right p-2">{formatPercent(displayedTotals.implied_mf)}</td>}
                  <td className="text-right p-2 text-green-900">{formatCurrency(displayedTotals.net_revenue)}</td>
                  <td className="text-right p-2 text-green-900">{formatPercent(displayedTotals.gpm)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* RIGHT PANEL: Net Fees Calculation */}
        <div className="card p-6">
          <div className="bg-red-600 text-white px-4 py-3 rounded-t-lg -mx-6 -mt-6 mb-6">
            <h3 className="text-lg font-bold">Net Fees Calculation</h3>
          </div>

          <div className="space-y-4">
            {/* Reader Costs */}
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-600 font-medium">Reader Costs</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-600">Qty</p>
                  <p className="font-semibold">{net_fees.reader_costs.qty}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-600">Rate</p>
                  <p className="font-semibold">{formatCurrency(net_fees.reader_costs.rate)}</p>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-red-200">
                <div className="flex justify-between font-bold text-red-900">
                  <span>Total Cost (Annual)</span>
                  <div className="text-right">
                    <p className="font-bold">{formatCurrency(net_fees.reader_costs.total_cost)}</p>
                    <p className="text-xs text-red-700">
                      {formatPercent((net_fees.reader_costs.total_cost / displayedTotals.total_fees) * 100)} of Gross Fees
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* SIM Costs */}
            {net_fees.sim_costs && net_fees.sim_costs.total_cost > 0 && (
              <div className="bg-red-50 p-4 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600 font-medium">SIM Card Costs</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-600">Qty</p>
                    <p className="font-semibold">{net_fees.sim_costs.qty}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-slate-600">Rate</p>
                    <p className="font-semibold">{formatCurrency(net_fees.sim_costs.rate)}/mo</p>
                  </div>
                </div>
                <div className="mt-2 pt-2 border-t border-red-200">
                  <div className="flex justify-between font-bold text-red-900">
                    <span>Total Cost (Annual)</span>
                    <div className="text-right">
                      <p className="font-bold">{formatCurrency(net_fees.sim_costs.total_cost)}</p>
                      <p className="text-xs text-red-700">
                        {formatPercent((net_fees.sim_costs.total_cost / displayedTotals.total_fees) * 100)} of Gross Fees
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Sales Commissions */}
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-600 font-medium">Sales Commissions</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-600">-</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-600">Rate</p>
                  <p className="font-semibold">{net_fees.sales_commissions.rate}%</p>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-red-200">
                <div className="flex justify-between font-bold text-red-900">
                  <span>Total Cost</span>
                  <div className="text-right">
                    <p className="font-bold">{formatCurrency(net_fees.sales_commissions.total_cost)}</p>
                    <p className="text-xs text-red-700">
                      {formatPercent((net_fees.sales_commissions.total_cost / displayedTotals.total_fees) * 100)} of Gross Fees
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Total Costs Pre-Partnership */}
            <div className="bg-amber-50 p-4 rounded-lg border-2 border-amber-400">
              <div className="flex justify-between items-center">
                <span className="font-bold text-amber-900">Total Costs Pre-Partnership</span>
                <span className="text-xl font-bold text-amber-900">
                  {formatCurrency(net_fees.total_costs_pre_partnership)}
                </span>
              </div>
            </div>

            {/* Net Fees Pre-Partnership */}
            <div className="bg-green-100 p-4 rounded-lg border-2 border-green-600">
              <div className="flex justify-between items-center">
                <span className="font-bold text-green-900">Net Fees Pre-Partnership</span>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-900">{formatCurrency(net_fees.net_fees_pre_partnership)}</p>
                  <p className="text-sm text-green-700">{formatPercent(net_fees.net_fees_pre_partnership_margin)}</p>
                </div>
              </div>
            </div>

            {/* Partnership Incentive */}
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-600 font-medium">Partnership Incentive</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-600">-</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-600">Rate</p>
                  <p className="font-semibold">{net_fees.partnership_incentive.rate}%</p>
                </div>
              </div>
              <div className="mt-2 pt-2 border-t border-red-200">
                <div className="flex justify-between font-bold text-red-900">
                  <span>Total Cost</span>
                  <div className="text-right">
                    <p className="font-bold">{formatCurrency(net_fees.partnership_incentive.total_cost)}</p>
                    <p className="text-xs text-red-700">
                      {formatPercent((net_fees.partnership_incentive.total_cost / displayedTotals.total_fees) * 100)} of Gross Fees
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Total Additional Costs */}
            <div className="bg-red-100 p-4 rounded-lg border border-red-400">
              <div className="flex justify-between font-bold text-red-900">
                <span>Total Additional Costs</span>
                <span className="text-lg">{formatCurrency(net_fees.total_additional_costs)}</span>
              </div>
            </div>

            {/* Final Net Fees */}
            <div className="bg-green-100 p-4 rounded-lg border-2 border-green-600">
              <div className="flex justify-between items-center">
                <span className="font-bold text-green-900">Net Fees</span>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-900">{formatCurrency(net_fees.net_fees)}</p>
                  <p className="text-sm text-green-700">{formatPercent(net_fees.net_fees_margin)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Incumbent Comparison Section */}
      {hasIncumbentComparison && incumbentComparison && incumbentComparison.incumbent_total_fees > 0 && (
        <div className="card-modern p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 mt-6">
          <h3 className="text-xl font-bold text-slate-900 mb-5 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-blue-600" />
            Proposed vs. Incumbent Comparison
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Total Savings */}
            <div className={`p-5 rounded-xl ${incumbentComparison.total_savings > 0 ? 'bg-green-100 border-2 border-green-400' : 'bg-red-100 border-2 border-red-400'}`}>
              <p className="text-sm font-semibold text-slate-700 mb-2">Total Savings</p>
              <p className={`text-3xl font-bold ${incumbentComparison.total_savings > 0 ? 'text-green-700' : 'text-red-700'}`}>
                {incumbentComparison.total_savings > 0 ? '+' : ''}{formatCurrency(incumbentComparison.total_savings)}
              </p>
              <p className="text-xs text-slate-600 mt-1">
                {incumbentComparison.savings_percentage.toFixed(2)}% {incumbentComparison.total_savings > 0 ? 'savings' : 'increase'} vs. incumbent
              </p>
            </div>
            
            {/* Incumbent Fees */}
            <div className="p-5 rounded-xl bg-slate-100 border-2 border-slate-300">
              <p className="text-sm font-semibold text-slate-700 mb-2">Incumbent Total Fees</p>
              <p className="text-3xl font-bold text-slate-700">
                {formatCurrency(incumbentComparison.incumbent_total_fees)}
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Current provider pricing
              </p>
            </div>
            
            {/* Proposed Fees */}
            <div className="p-5 rounded-xl bg-blue-100 border-2 border-blue-400">
              <p className="text-sm font-semibold text-slate-700 mb-2">Proposed Total Fees</p>
              <p className="text-3xl font-bold text-blue-700">
                {formatCurrency(incumbentComparison.proposed_total_fees)}
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Government Window pricing
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
